// SPDX-License-Identifier: AGPL-3.0-or-later

import type {MessageSwipeActionId} from '@app/features/accessibility/state/MessageSwipeActionPrefs';

export type {
	MessageSwipeActionId,
	MessageSwipeVariant,
} from '@app/features/accessibility/state/MessageSwipeActionPrefs';
export {
	DEFAULT_MESSAGE_SWIPE_OTHERS_LEFT_ACTION,
	DEFAULT_MESSAGE_SWIPE_OTHERS_RIGHT_ACTION,
	DEFAULT_MESSAGE_SWIPE_OWN_LEFT_ACTION,
	DEFAULT_MESSAGE_SWIPE_OWN_RIGHT_ACTION,
	getMessageSwipeActionIdsForVariant,
	isMessageSwipeActionId,
	MESSAGE_SWIPE_ACTION_DEFS,
	MESSAGE_SWIPE_ACTION_IDS,
	MESSAGE_SWIPE_OTHERS_ACTION_IDS,
	MESSAGE_SWIPE_OWN_ACTION_IDS,
	normalizeMessageSwipeAction,
} from '@app/features/accessibility/state/MessageSwipeActionPrefs';

export const SWIPE_AXIS_LOCK_THRESHOLD_PX = 10;
export const SWIPE_COMMIT_DISTANCE_PX = 64;
export const SWIPE_COMMIT_VELOCITY_PX_MS = 0.45;
export const SWIPE_MAX_REVEAL_PX = 80;
export const SWIPE_VIBRATE_MS = 12;

export type SwipeAxis = 'none' | 'horizontal' | 'vertical';

export interface MessageSwipeRevealStyle {
	backgroundColor: string;
	color: string;
}

/**
 * Fixed solid pairs (not theme surface vars) so icon-on-reveal stays WCAG AA
 * for non-text contrast (≥3:1) and preferably ≥4.5:1 in both light and dark UI.
 */
const MESSAGE_SWIPE_REVEAL_STYLES: Record<MessageSwipeActionId, MessageSwipeRevealStyle> = {
	none: {backgroundColor: 'transparent', color: 'transparent'},
	reply: {backgroundColor: '#1d4ed8', color: '#ffffff'},
	react: {backgroundColor: '#a16207', color: '#ffffff'},
	edit: {backgroundColor: '#b45309', color: '#ffffff'},
	delete: {backgroundColor: '#b91c1c', color: '#ffffff'},
	forward: {backgroundColor: '#15803d', color: '#ffffff'},
	pin: {backgroundColor: '#b45309', color: '#ffffff'},
	bookmark: {backgroundColor: '#15803d', color: '#ffffff'},
	remind: {backgroundColor: '#0f766e', color: '#ffffff'},
	mark_unread: {backgroundColor: '#3730a3', color: '#ffffff'},
	copy_message: {backgroundColor: '#3730a3', color: '#ffffff'},
	open_channel_list: {backgroundColor: '#334155', color: '#ffffff'},
};

export function getMessageSwipeRevealStyle(action: MessageSwipeActionId): MessageSwipeRevealStyle {
	return MESSAGE_SWIPE_REVEAL_STYLES[action];
}

export function resolveSwipeAxis(dx: number, dy: number, lockThreshold = SWIPE_AXIS_LOCK_THRESHOLD_PX): SwipeAxis {
	const absX = Math.abs(dx);
	const absY = Math.abs(dy);
	if (absX < lockThreshold && absY < lockThreshold) {
		return 'none';
	}
	return absX >= absY ? 'horizontal' : 'vertical';
}

export function shouldCommitSwipe(
	offsetPx: number,
	velocityXPxPerMs: number,
	commitDistance = SWIPE_COMMIT_DISTANCE_PX,
	commitVelocity = SWIPE_COMMIT_VELOCITY_PX_MS,
): boolean {
	if (Math.abs(offsetPx) >= commitDistance) {
		return true;
	}
	// Reverse flicks must cancel, not commit — velocity must share the offset's sign.
	return (
		offsetPx !== 0 &&
		Math.abs(velocityXPxPerMs) >= commitVelocity &&
		Math.sign(velocityXPxPerMs) === Math.sign(offsetPx)
	);
}

export function clampSwipeOffset(offsetPx: number, maxReveal = SWIPE_MAX_REVEAL_PX): number {
	if (!Number.isFinite(offsetPx)) {
		return 0;
	}
	return Math.max(-maxReveal, Math.min(maxReveal, offsetPx));
}

export function vibrateMessageSwipeCommit(durationMs = SWIPE_VIBRATE_MS): void {
	if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') {
		return;
	}
	try {
		navigator.vibrate(durationMs);
	} catch {
		// Ignore browsers that expose vibrate but reject the call.
	}
}
